import { FieldStateRef } from '@/classes/FieldReference';
import { FieldStatus } from '@/classes/FieldStatus';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { EditionModeRef } from '@/classes/ScenarioReference';
import { StepConfig } from '@/classes/StepConfig';
import { Route } from 'vue-router';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import stepperHelper from '@/helpers/rulesModules/stepperHelper';
import { checkFieldHasError } from '@/helpers/checkFieldHasError';
import { IRule } from '@/classes/rules/IRule';
import { InjectionModelRef } from '@/dto/scenario/ScenarioReference';
import { RevenuesTypeRef } from '@/dto/scenario/eco/EcoReference';

/**
 * This store module handles the steps declaration and their status updates, in order to know
 * if they are available, controlled, with errors, etc.
 */

/**
 * Primary declaration of all steps.
 */
function stepIsBeforeSizing(step: StepConfig) {
  return stepperHelper.stepsBeforeSizing.includes(step.key);
}

function checkFieldIsAvailableWithNotSetParent(
  fieldPath: string,
  step: StepConfig,
  fieldErrorMapByPath: Map<string, FieldStatus>,
  scenario: ScenarioExt
): boolean {
  const fieldRule = step.rulesByPath.get(fieldPath);
  const fieldIsAvailable =
    fieldRule === undefined ||
    (scenario !== null && fieldRule.isAvailable(scenario));
  if (!fieldIsAvailable) {
    return false; // the field is not available
  }
  const fieldPathSteps = fieldPath.split('.');
  let currentPath = null; // Progressive path : 'a', 'a.b', 'a.b.c'
  for (const pathStep of fieldPathSteps) {
    currentPath = currentPath ? `${currentPath}.${pathStep}` : pathStep;
    const fieldErrorStatus = fieldErrorMapByPath.get(currentPath);
    if (
      fieldErrorStatus &&
      fieldErrorStatus.state === FieldStateRef.not_set
    ) {
      return true; // the field should be available but one of its parent is not set.
    }
  }
  return false; // the field is available, all its parents are set.
}

/**
 * Check if a step has errors in its fields, according to a fieldStatus map by field path.
 * @param step the checked step
 * @param scenario current scenario state
 * @param fieldErrorMapByPath map of field path - field error
 */
function checkStepHasError(
  step: StepConfig,
  scenario: ScenarioExt,
  fieldErrorMapByPath: Map<string, FieldStatus>
): boolean {
  // We search for a field with an error
  const fieldWithError = step.fields
    .find(fieldPath => checkFieldHasError(fieldPath, step, fieldErrorMapByPath));
  if (fieldWithError) {
    return true; // no need to go further
  }
  // No error that strictly matches one of the fields, we search for available fields with not set parents
  const fieldIsAvailableWithNotSetParent = step.fields
    .find(fieldPath => checkFieldIsAvailableWithNotSetParent(fieldPath, step, fieldErrorMapByPath, scenario));
  if (fieldIsAvailableWithNotSetParent) {
    return true; // no need to go further
  }
  return false;
}

interface Dependencies {
  steps: StepConfig[]
  findRule: (path: string[]) => IRule | undefined
}

export const buildStepperModule = (dependencies: Dependencies) => {
  const { steps, findRule } = dependencies;

  @Module({
    namespaced: true
  })
  class StepperModule extends VuexModule {
    private stepConfiguration: StepConfig[] = steps;

    /**
     * Current route of navigation
     */
    private currentRoute: Route | null = null;

    @Action
    public setNextRoute(payload: Route) {
      this.context.commit('setCurrentRoute', payload);
    }

    /**
     * Reset steps as if they were not visited yet. Called on change of scenario.
     */
    @Action
    public resetVisitedSteps() {
      for (const step of this.getStepConfiguration) {
        step.hasBeenVisited = false;
      }
    }

    /**
     * Add rules maps to the step declaration
     */
    get getStepConfiguration(): StepConfig[] {
      return this.stepConfiguration.map(s => {
        const ruleByPath = [s.key, ...s.fields]
          .map((path) => ([path, findRule(path.split('.'))] as const))
          .filter(([, rule]) => rule !== undefined);
        return new StepConfig({
          ...s,
          rules: new Map(ruleByPath)
        });
      });
    }

    get getStepMapByKey(): Map<string, StepConfig> {
      return new Map(this.getStepConfiguration.map(m => [m.key, m]));
    }

    // scenario getters
    get getScenarioConf(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    get editionMode(): EditionModeRef {
      return this.context.rootGetters['scenario/getEditionMode'];
    }

    get fieldErrors(): FieldStatus[] {
      return this.context.rootGetters['scenario/getFieldErrors'];
    }

    get fieldErrorsMapByPath(): Map<string, FieldStatus> {
      return new Map(this.fieldErrors.map(err => [err.path.join('.'), err]));
    }

    // stepper logic
    /**
     * In Edition mode we control all steps. In creation mode we control only steps before the current step, or visited steps.
     */
    get mustControlNextStepsErrors(): boolean {
      return this.editionMode === EditionModeRef.edition || this.editionMode === EditionModeRef.creation;
    }

    /**
     * Index in current navigation of the original step. Used in the getUpdatedSteps getter
     */
    get currentIndex(): number {
      return this.currentRoute
        ? this.getStepConfiguration.findIndex(
          s => s.goTo === this.currentRoute!.path
        )
        : -1;
    }

    /**
     * Index in current navigation of the step in the updatedSteps collection. Used for display purposes in components.
     */
    get currentUpdatedIndex(): number {
      return this.currentRoute
        ? this.getUpdatedSteps.findIndex(s => s.goTo === this.currentRoute!.path)
        : -1;
    }

    /**
     * Indicates if a report is currently available
     */
    get currentReportStatus(): boolean {
      return (
        !!this.context.rootGetters['scenario/getTechnicalResult'] ||
        !!this.context.rootGetters['scenario/getEconomicalResult']
      );
    }

    get renFrReportStatus(): boolean {
      return (
        !!this.context.rootGetters['scenario/getRenFrAlgoResult']
      );
    }

    /**
     * Errors that define sizing step access
     * are in stepperHelper.excludedErrorsForSizingValidation
     */
    // get getErrorsForSizingValidation(): boolean {
    //   const errorThatBlockSizingAccess = this.fieldErrors.find(e => {
    //     if (e.path.length && e.path[0] === 'sizing') {
    //       return false; // sizing error does not block
    //     }
    //     if (stepperHelper.excludedErrorsForSizingValidation.includes(e.path.join('.'))) {
    //       return false; // this error is specifically excluded
    //     }
    //     if (!e.ruleMessages || e.ruleMessages.length === 0) {
    //       return true; // this error is not excluded and has no sub-causes, so it blocks
    //     }
    //     const sourcesOfErrors = e.ruleMessages.flatMap(msg => msg.fields);
    //     const notExcludedSourceOfError = sourcesOfErrors.find(source => !stepperHelper.excludedErrorsForSizingValidation.includes(source));
    //     return !!notExcludedSourceOfError; // if this error has at least one sub-error which is not excluded, it blocks. Else, it does not.
    //   });
    //   return !!errorThatBlockSizingAccess;
    // }

    /**
     * Main logic of this module.
     * Update the steps properties, then filter them on their availability, and shallowCopy them to adjust indexes
     */
    get getUpdatedSteps(): StepConfig[] {
      const scenario = this.getScenarioConf;
      const initializedSteps = this.getStepConfiguration.map((s, i) => {
        // STEPCONFIG AVAILABLE
        switch (s.key) {
          case 'eco_report': {
            s.available = this.currentReportStatus && !scenario.isRenFrance;
            break;
          }
          case 'executive_report': {
            s.available = this.currentReportStatus && !scenario.pv?.isRenExplorer && !scenario.isRenFrance;
            break;
          }
          case 'tech_report': {
            s.available = this.context.rootGetters['scenario/hasTechnicalReport'];
            break;
          }
          case 'client_result': {
            s.available = this.currentReportStatus && scenario.eco?.revenues.revenuesTypeRef !== RevenuesTypeRef.savings && !scenario.hasInjectionType(InjectionModelRef.total_injection);
            break;
          }
          case 'ren_fr_eco_report': {
            s.available = scenario.isRenFrance && this.renFrReportStatus;
            break;
          }
          default: {
            const rule = s.rulesByPath.get(s.key);
            s.available = rule?.isAvailable(scenario) ?? true;
          }
        }
        return s;
      }).map((s, i) => {
        // STEPCONFIG MUSTBECONTROLLED
        s.mustBeControlled =
            s.hasBeenVisited ||
            this.mustControlNextStepsErrors ||
            i < this.currentIndex;
        // STEPCONFIG HASERRORS
        s.hasErrors = false; // initialized to false.
        if (s.mustBeControlled) {
          s.hasErrors = checkStepHasError(s, scenario, this.fieldErrorsMapByPath);
        }
        return s;
      });
      const stepsBeforeSizing = initializedSteps.filter(s => s.available && stepIsBeforeSizing(s));
      const allStepBeforeSizingAreControlled = stepsBeforeSizing.every(s => s.mustBeControlled);
      const stepBeforeSizingWithError = stepsBeforeSizing.find(s => s.hasErrors);
      const sizingStep = initializedSteps.find(s => s.key === 'sizing');
      if (sizingStep) {
        sizingStep.available = !scenario.hasInjectionType(InjectionModelRef.total_injection) && allStepBeforeSizingAreControlled && !stepBeforeSizingWithError;
      }
      return initializedSteps
      // Filter on available steps
        .filter(s => s.available)
      // remap & shallowCopy to adjust indexes
        .map((s, i) => {
          s.index = i;
          return s;
        });
    }

    @Mutation
    private setCurrentRoute(payload: Route) {
      this.currentRoute = payload;
    }
  }

  return StepperModule;
};
